/**
 * PasswordInput — a drop-in replacement for `<Input type="password" />` with
 * an eye-icon toggle that reveals/hides the value. Everything else (value,
 * onChange, placeholder, data-testid, minLength, required, etc.) passes
 * through to the underlying Input so existing call sites barely change.
 *
 * Usage:
 *   <PasswordInput value={pw} onChange={e => setPw(e.target.value)}
 *                  data-testid="login-password-input" required />
 */
import React, { useState, forwardRef } from 'react';
import { Eye, EyeClosed } from 'iconoir-react';
import { Input } from './input';

const PasswordInput = forwardRef(function PasswordInput(
  { className = '', 'data-testid': testid, ...rest }, ref,
) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        ref={ref}
        {...rest}
        type={visible ? 'text' : 'password'}
        className={`${className} pr-10`}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted hover:text-charcoal transition-colors"
        tabIndex={-1}
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        {visible ? (
          <EyeClosed className="w-4 h-4" strokeWidth={1.75} />
        ) : (
          <Eye className="w-4 h-4" strokeWidth={1.75} />
        )}
      </button>
    </div>
  );
});

export default PasswordInput;
